import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

export interface BulkImportResultRow {
  /** Stable per-row key. */
  id: string;
  /** Primary text , typically the email or name. */
  primary: string;
  /** Optional secondary text , e.g. role or message. */
  secondary?: string;
}

export interface BulkImportResultBucket {
  /** Stable key , used for column id and snackbar key. */
  id: string;
  /** Column header label. */
  label: string;
  /** Color theme for the count chip. */
  color: "success" | "warning" | "error" | "default" | "info";
  /** Optional explanatory subtext under the header. */
  description?: string;
  rows: BulkImportResultRow[];
}

interface BulkImportResultTableProps {
  buckets: BulkImportResultBucket[];
  /** Optional CTA in the footer (e.g., "Download failures CSV"). */
  footerActions?: ReactNode;
}

/**
 * Result bucket layout for bulk-import flows.
 *
 * Buckets render as stacked vertical sections, one per status (e.g.
 * Successfully Added / Already Exists / Skipped / Failed). Empty
 * buckets collapse to a one-line "0 X" summary; non-empty buckets show
 * a count chip and a scroll-bounded list of rows.
 *
 * Replaces an earlier side-by-side column layout that wrapped awkwardly
 * once a fourth bucket (Failed) was added , the columns ended up in two
 * rows of three, with the trailing Failed bucket landing in a horizontal
 * strip below. The vertical stack always reads top-to-bottom regardless
 * of bucket count.
 */
export function BulkImportResultTable({
  buckets,
  footerActions,
}: BulkImportResultTableProps) {
  // Hide empty buckets entirely. The earlier "show 0-count buckets at
  // reduced opacity" pattern was meant to reassure the operator that
  // every category got considered, but in practice the dimmed cards
  // just added noise , operators read what's there, not what's absent.
  const visibleBuckets = buckets.filter((b) => b.rows.length > 0);
  return (
    <Stack spacing={1.5}>
      {visibleBuckets.map((bucket) => {
        const empty = bucket.rows.length === 0;
        const chipColor =
          bucket.color === "default" ? undefined : bucket.color;
        return (
          <Paper
            key={bucket.id}
            variant="outlined"
            sx={{
              borderRadius: 1.5,
              opacity: empty ? 0.6 : 1,
            }}
            data-testid={`bulk-import-bucket-${bucket.id}`}
          >
            <Box
              sx={{
                px: 2,
                py: 1.25,
                borderBottom: empty ? 0 : "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                flexWrap="wrap"
              >
                <Chip
                  label={bucket.rows.length}
                  size="small"
                  color={chipColor}
                  variant={empty ? "outlined" : "filled"}
                  sx={{ minWidth: 32, fontWeight: 600 }}
                />
                <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                  {bucket.label}
                </Typography>
                {bucket.description && (
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      color: "text.secondary",
                      ml: "auto",
                    }}
                  >
                    {bucket.description}
                  </Typography>
                )}
              </Stack>
            </Box>
            {!empty && (
              <Box sx={{ maxHeight: 220, overflow: "auto", px: 1.5, py: 1 }}>
                <Stack spacing={0.25} divider={null}>
                  {bucket.rows.map((r) => (
                    <Box
                      key={r.id}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr auto",
                        columnGap: 1.5,
                        alignItems: "baseline",
                        px: 0.5,
                        py: 0.5,
                      }}
                    >
                      <Typography
                        sx={{ fontSize: "0.875rem", lineHeight: 1.35 }}
                      >
                        {r.primary}
                      </Typography>
                      {r.secondary && (
                        <Typography
                          sx={{
                            fontSize: "0.78rem",
                            color: "text.secondary",
                            lineHeight: 1.35,
                            textAlign: "right",
                          }}
                        >
                          {r.secondary}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Paper>
        );
      })}
      {footerActions && (
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          {footerActions}
        </Stack>
      )}
    </Stack>
  );
}
